import React from 'react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Employee } from '../../../utils/types';
import { TaskFormSection } from './taskUi';

export type TaskFormValues = {
  title: string;
  description: string;
  start_date: string;
  due_date: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'in_progress' | 'completed' | 'on_hold' | 'cancelled';
  assigned_to: string;
  remarks: string;
};

export const defaultTaskFormValues: TaskFormValues = {
  title: '',
  description: '',
  start_date: '',
  due_date: '',
  priority: 'medium',
  status: 'pending',
  assigned_to: '',
  remarks: '',
};

type TaskFormFieldsProps = {
  values: TaskFormValues;
  employees: Employee[];
  onChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  isEdit?: boolean;
};

const fieldStyles = {
  borderRadius: '16px',
  bg: 'gray.50',
  borderColor: 'gray.200',
  _focusVisible: {
    borderColor: 'green.400',
    boxShadow: '0 0 0 1px rgba(22, 101, 52, 0.28)',
    bg: 'white',
  },
};

export const TaskFormFields = ({
  values,
  employees,
  onChange,
  isEdit = false,
}: TaskFormFieldsProps) => (
  <Stack spacing={5}>
    <TaskFormSection
      title="Task basics"
      description="Start with the work itself: what needs to happen and the context the assignee should see first."
    >
      <FormControl isRequired>
        <FormLabel>Title</FormLabel>
        <Input
          name="title"
          value={values.title}
          onChange={onChange}
          placeholder="Follow up with Acme proposal"
          {...fieldStyles}
        />
      </FormControl>

      <FormControl>
        <FormLabel>Description</FormLabel>
        <Textarea
          name="description"
          value={values.description}
          onChange={onChange}
          placeholder="Add the full task brief, expected outcome, and any context that should not be missed."
          minH="132px"
          resize="vertical"
          {...fieldStyles}
        />
      </FormControl>
    </TaskFormSection>

    <TaskFormSection
      title="Ownership and schedule"
      description="Set who owns the task and when it should start and finish."
    >
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel>Assigned to</FormLabel>
          <Select
            name="assigned_to"
            value={values.assigned_to}
            onChange={onChange}
            placeholder="Select employee"
            {...fieldStyles}
          >
            {employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.user?.first_name} {employee.user?.last_name} {employee.role ? `• ${employee.role}` : ''}
              </option>
            ))}
          </Select>
          <FormHelperText>Choose the teammate responsible for moving this task forward.</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>Status</FormLabel>
          <Select name="status" value={values.status} onChange={onChange} {...fieldStyles}>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="on_hold">On Hold</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Start date</FormLabel>
          <Input type="date" name="start_date" value={values.start_date} onChange={onChange} {...fieldStyles} />
        </FormControl>

        <FormControl>
          <FormLabel>Due date</FormLabel>
          <Input type="date" name="due_date" value={values.due_date} onChange={onChange} {...fieldStyles} />
          <FormHelperText>Use a due date to keep the work visible and measurable.</FormHelperText>
        </FormControl>
      </SimpleGrid>
    </TaskFormSection>

    <TaskFormSection
      title="Priority and notes"
      description="Clarify how urgent the task is and capture any working notes that matter."
    >
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl>
          <FormLabel>Priority</FormLabel>
          <Select name="priority" value={values.priority} onChange={onChange} {...fieldStyles}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel>Working notes</FormLabel>
          <Textarea
            name="remarks"
            value={values.remarks}
            onChange={onChange}
            placeholder="Internal notes, blockers, handoff detail, or next-step context."
            minH="120px"
            resize="vertical"
            {...fieldStyles}
          />
        </FormControl>
      </SimpleGrid>
    </TaskFormSection>

    {isEdit ? (
      <Text fontSize="sm" color="gray.500">
        Update the fields that changed. Leave the rest as they are.
      </Text>
    ) : (
      <Text fontSize="sm" color="gray.500">
        A task becomes easier to execute when the title, owner, due date, and current status are clear from the start.
      </Text>
    )}
  </Stack>
);
